import { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'
export type AvatarVariant = 'accent' | 'system' | 'warning' | 'success'

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Full name — used to compute initials if no children provided. */
  name?: string
  size?: AvatarSize
  variant?: AvatarVariant
  /** Explicit initials override. */
  initials?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-5 w-5 text-2xs',
  md: 'h-7 w-7 text-xs',
  lg: 'h-9 w-9 text-sm',
  xl: 'h-12 w-12 text-lg',
}

const variantClasses: Record<AvatarVariant, string> = {
  accent: 'bg-accent-subtle text-accent-text',
  system: 'bg-info-bg text-info-text',
  warning: 'bg-warning-bg text-warning-text',
  success: 'bg-success-bg text-success-text',
}

function computeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, size = 'md', variant = 'accent', initials, className, children, ...rest },
  ref,
) {
  const label = children ?? initials ?? (name ? computeInitials(name) : '?')
  return (
    <span
      ref={ref}
      aria-label={name}
      data-size={size}
      data-variant={variant}
      className={cn(
        'inline-flex flex-shrink-0 select-none items-center justify-center rounded-full font-semibold',
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {label}
    </span>
  )
})

/**
 * Overlapping avatar stack — used in participant lists, assignee indicators.
 * Last avatar sits on top; add `…+N` chip as final child for overflow.
 */
export function AvatarStack({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span className={cn('inline-flex items-center [&>*]:border [&>*]:border-surface [&>*+*]:-ml-2', className)}>
      {children}
    </span>
  )
}
